<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">

            <!-- Dropdown -->
            <Button
              v-if="getUserPermission('POST_INWARD_WITHOUT_GATEWAY_CHECK') && selectedPendingIds.length !== 0"
              label="Process Pending Request"
              size="md"
              class="mr-1"
              @click="onProcessPendingRequest"
            />

            <Button
              label="Filter"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              label="Reset Filter"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="customerInfoRef"
      :items="inwardTransferList"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="tableHeaders"
      primary-key="id"
      show-empty
      empty-text="No records available"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(select)="data">
        <b-form-group class="mt-2">
          <b-form-checkbox
            v-if="data.item.status === 'Pending'"
            v-model="selectedPendingIds"
            :value="data.item.id"
          />
        </b-form-group>
      </template>
      <template #cell(dateCreated)="data">
        {{ toDateWithCustomTime(data.value) }}
      </template>

      <template #cell(amount)="data">
        {{ toBalance(data.value) }}
      </template>
      <template #cell(isAwaitingReversal)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value ? 'Yes' : 'No')"
        >
          {{ data.value ? 'Yes' : 'No' }}
        </b-badge>
      </template>
      <template #cell(isReversed)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value ? 'Yes' : 'No')"
        >
          {{ data.value ? 'Yes' : 'No' }}
        </b-badge>
      </template>
      <template #cell(status)="data">
        <b-badge
          v-if="data.value"
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      ok-title="Apply"
      no-close-on-backdrop
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      title="Filters"
      modal-footer
      @ok="fetchInwardTransfer"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Name -->
        <b-col md="6">
          <InputField
            id="account-name"
            v-model="filter.accountName"
            label="Originator Account Name"
            placeholder="Enter account name"
            type="text"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Account Name -->

        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            label="Originator Account Number"
            placeholder="Enter account number"
            type="number"
          />
        </b-col>
        <!-- Account Number -->

        <!-- Beneficiary Account Number -->
        <b-col md="6">
          <InputField
            id="beneficiary-account-number"
            v-model="filter.beneficiaryAccountNumber"
            label="Beneficiary Account Number"
            placeholder="Enter Beneficiary Account Number"
            type="number"
          />
        </b-col>
        <!-- Beneficiary Account Number -->

        <b-col md="6">
          <InputField
            id="session-id"
            v-model="filter.sessionID"
            label="Session ID"
            placeholder="Enter Session ID"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="posting-reference"
            v-model="filter.postingsReference"
            label="Posting Reference"
            placeholder="Enter Posting Reference"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="payment-reference"
            v-model="filter.paymentReference"
            label="Payment Reference"
            placeholder="Enter Payment Reference"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="name-enquiry-ref"
            v-model="filter.nameEnquiryRef"
            label="Name Enquiry Reference"
            placeholder="Enter Name Enquiry Reference"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="gateway-provider"
            v-model="filter.gateway"
            label="Gateway"
            :options="gatewayProviderOptions"
            item="label"
            :reduce="option => option"
            placeholder="Select"
          />
        </b-col>
        <b-col md="6">
          <DatePickerField
            id="transaction-date-from"
            v-model="filter.transactionDateFrom"
            placeholder="Select date"
            label="Transaction Date From"
            :config="datePickerConfig"
          />
        </b-col>
        <b-col md="6">
          <DatePickerField
            id="transaction-date-to"
            v-model="filter.transactionDateTo"
            placeholder="Select date"
            label="Transaction Date To"
            :config="datePickerConfig"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="response-code"
            v-model="filter.responseCode"
            label="Response Code"
            placeholder="Enter Response Code"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="is-awaiting-reversal"
            v-model="filter.isAwaitingReversal"
            label="Is Awaiting Reversal?"
            placeholder="Select"
            :options="isAwaitingReversalOptions"
            item="label"
            :reduce="option => option"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="is-reversed"
            v-model="filter.isReversed"
            label="Is Reversed?"
            placeholder="Select"
            :options="isReversedOptions"
            item="label"
            :reduce="option => option"
          />
        </b-col>
        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            label="Status"
            placeholder="Select"
            :options="statusOptions"
            item="label"
            :reduce="option => option"
          />
        </b-col>
        <!-- Status -->

      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable,
  BSpinner, BBadge, BFormGroup, BFormCheckbox,
} from 'bootstrap-vue'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions } from 'vuex'
import TablePagination from '@/views/components/table/TablePagination.vue'
import getStatusColor from '../../../../../helpers/getStatusColor'
import { toAmount, toBalance, toFormatAmount } from '@/helpers/formatAmount'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import {
  toDateWithYear, toDateWithTime, toDateTimeFormat, toDateWithCustomTime,
} from '../../../../../helpers/formatDate'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import Button from '../../../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    BBadge,
    BFormGroup,
    BFormCheckbox,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    FullScreenSpinner,
    DatePickerField,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      toDateWithTime,
      toDateTimeFormat,
      toDateWithCustomTime,
      getStatusColor,
      toAmount,
      toFormatAmount,
      toBalance,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'select' },
        { key: 'originatorAccountName', sortable: true, label: 'Originator Account Name' },
        { key: 'originatorAccountNumber', sortable: true, label: 'Originator Account Number' },
        { key: 'originatorBankVerificationNumber', sortable: true, label: 'Originator BVN' },
        { key: 'originatorKYCLevel', sortable: true, label: 'Originator KYC Level' },
        { key: 'originatorBankName', sortable: true, label: 'Originator Bank Name' },
        { key: 'beneficiaryAccountNumber', sortable: true, label: 'Beneficiary Account No.' },
        { key: 'beneficiaryAccountName', sortable: true, label: 'Beneficiary Account Name' },
        { key: 'beneficiaryBankVerificationNumber', sortable: true, label: 'Beneficiary BVN' },
        { key: 'amount', sortable: true },
        { key: 'destinationInstitutionCode', sortable: true },
        { key: 'sessionID', sortable: true },
        { key: 'postingsReference', sortable: true },
        { key: 'nameEnquiryRef', sortable: true },
        { key: 'originatorNarration', sortable: true },
        { key: 'paymentReference', sortable: true },
        { key: 'responseCode', sortable: true },
        { key: 'gateway', sortable: true, label: 'Transfer Gateway' },
        { key: 'tsqResponseCode', sortable: true },
        { key: 'retryCount', sortable: true },
        { key: 'isAwaitingReversal', sortable: true },
        { key: 'isReversed', sortable: true },
        { key: 'dateCreated', sortable: true, label: 'Transaction Date' },
        { key: 'status', sortable: true },
      ],
      statusOptions: [
        'Processed',
        'Pending',
        'Failed',
        'Reversed',
      ],
      inwardTransferList: [],
      selectedPendingIds: [],
      filter: {
        accountName: null,
        accountNumber: null,
        beneficiaryAccountNumber: null,
        status: null,
        transactionDateFrom: null,
        transactionDateTo: null,
        paymentReference: null,
        postingReference: null,
        responseCode: null,
        nameEnquiryRef: null,
        sessionID: null,
        isAwaitingReversal: false,
        isReversed: false,
        gateway: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
      },
      isAwaitingReversalOptions: ['Yes', 'No'],
      isReversedOptions: ['Yes', 'No'],
      showSpinner: false,
      gatewayProviderOptions: [],
    }
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchInwardTransfer()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchInwardTransfer()
    },
  },
  mounted() {
    this.fetchInwardTransfer()
    this.fetchGatewayProviders()
  },
  methods: {
    ...mapActions({
      getInwardTransfers: 'eChannels/getInwardTransfers',
      getGatewayProviders: 'eChannels/getGatewayProviders',
      performPendingRequestAction: 'eChannels/performPendingRequestAction',
    }),

    onProcessPendingRequest() {
      const permission = 'POST_INWARD_WITHOUT_GATEWAY_CHECK'

      const performPendingRequest = async () => {
        try {
          this.showSpinner = true
          const formData = {
            ids: this.selectedPendingIds.toString(),
            isSessionIDPassed: false,
          }
          const response = await this.performPendingRequestAction({
            formData, permission,
          })
          if (response.status === 200) {
            this.fetchInwardTransfer()
            this.selectedPendingIds = []
            this.alertSuccess({ text: 'Your request to credit the selected accounts has been recieved and will be processed shortly.' })
            this.showSpinner = false
          }
        } catch (error) {
          this.handleError(error)
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(performPendingRequest, { title: '', text: 'You are about to credit the selected accounts without verifying the current transaction status from the gateway. Are you sure you want continue?' })
    },
    async fetchInwardTransfer() {
      try {
        this.isBusy = true
        const filter = {
          ...this.filter,
          isAwaitingReversal: this.filter.isAwaitingReversal === 'Yes',
          isReversed: this.filter.isReversed === 'Yes',
        }
        const response = await this.getInwardTransfers({
          search: filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: 'VIEW_INWARD_TRANSFER_REPORT',
        })
        const { status, data } = response
        if (status === 200) {
          this.isBusy = false
          this.inwardTransferList = data.data ? data.data.map(item => ({
            ...item,
            amount: toBalance(item.amount),
            fee: toBalance(item.fee),
          })) : []
          this.totalCount = data.recordCount
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async fetchGatewayProviders() {
      try {
        const response = await this.getGatewayProviders({ permission: 'VIEW_INWARD_TRANSFER_REPORT' })
        const { status, data } = await response
        if (status === 200 && data) {
          this.gatewayProviderOptions = data
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    resetFilters() {
      this.filter = {
        ...null,
      }
    },
    resetFilter() {
      this.filter = {
        ...null,
      }
      this.selectedPendingIds = []
      this.fetchInwardTransfer()
    },

  },
}
</script>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
